import { useState, useEffect } from "react";
import { Controller, FieldError, useFormContext, useFieldArray } from "react-hook-form";
import { mdiMinusCircleOutline, mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";

import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import { IFundingRequestForm } from "models/HaulPay";
import { ICreateLoadForm } from "models/Load";
import styles from "./style.module.scss";

interface DestinationInputGroupProps extends React.HTMLAttributes<HTMLUListElement> {}

export default function DestinationInputGroup({ ...props }: DestinationInputGroupProps) {
  const {
    control,
    register,
    formState: { errors }
  } = useFormContext<ICreateLoadForm | IFundingRequestForm>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "destinationLocations"
  });

  const [isVisibleDestination, setIsVisibleDestination] = useState<boolean[]>(
    fields.map(() => false)
  );

  useEffect(() => {
    setIsVisibleDestination((prev) => {
      const newVisibility = [...prev];
      while (newVisibility.length < fields.length) {
        newVisibility.push(false);
      }
      while (newVisibility.length > fields.length) {
        newVisibility.pop();
      }
      return newVisibility;
    });
  }, [fields.length]);

  const toggleVisibility = (index: number) => {
    setIsVisibleDestination((prev) => {
      const newVisibility = [...prev];
      newVisibility[index] = !newVisibility[index];
      return newVisibility;
    });
  };

  const addLocation = () => {
    append({ destination: null, destinationName: "", destinationInstruction: "" });
  };

  const removeLocation = (index: number) => {
    remove(index);
  };

  return (
    <ul className={props.className}>
      {fields.map((field, index) => (
        <li key={field.id} className={styles.destinationInputGroupContainer}>
          <Controller
            name={`destinationLocations.${index}.destination`}
            control={control}
            render={({ field: { value = field.destination, onChange, onBlur } }) => (
              <GoogleAutoComplete
                value={value}
                error={
                  (errors.destinationLocations?.[index]?.destination?.state as FieldError) ||
                  (errors.destinationLocations?.[index]?.destination?.city as FieldError) ||
                  (errors.destinationLocations?.[index]?.destination as FieldError)
                }
                onChange={onChange}
                onBlur={onBlur}
                label={`* Destination ${fields.length > 1 ? index + 1 : ""}`}
                containerClass="flex-1 desktop:col-span-1"
              />
            )}
          />
          <div className="self-start w-full">
            <Input
              name={`destinationLocations.${index}.destinationName`}
              register={register}
              label={
                <>
                  <span className="pl-1">{`Destination Name ${
                    fields.length > 1 ? index + 1 : ""
                  } - `}</span>
                  <span className="italic text-xxs">Optional</span>
                </>
              }
            />
          </div>
          <div className={styles.addInstruction}>
            <button
              type="button"
              className={
                !isVisibleDestination[index] ? "text-left underline" : "text-left text-primary"
              }
              onClick={() => toggleVisibility(index)}>
              {isVisibleDestination[index] ? "- Hide Instructions" : "+ Add Instructions"}
            </button>
            <Input
              name={`destinationLocations.${index}.destinationInstruction`}
              register={register}
              className={isVisibleDestination[index] ? "" : "hidden"}
            />
          </div>
          <div className="ml-4 flex space-x-2 items-center justify-end">
            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => removeLocation(index)}
                className="text-gray-400 hover:text-red-500 flex items-center"
                aria-label={`Remove Destination ${index + 1}`}>
                <Icon path={mdiMinusCircleOutline} size={1} />
              </button>
            )}
            {index === fields.length - 1 && (
              <button
                type="button"
                onClick={addLocation}
                className="text-gray-400 hover:text-green-500 flex items-center"
                aria-label="Add Destination">
                <Icon path={mdiPlusCircleOutline} size={1} />
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
